import React, { useEffect, useRef } from 'react';
import { GuidedProgressBar } from './GuidedProgressBar';

export interface GuidedRestProps {
  done: number;
  total: number;
  /** Called when the rest screen advances — either via tap or after the
      auto-advance timer fires. Parent transitions to the next task. */
  onAdvance: () => void;
  /** Auto-advance delay in seconds. Defaults to 6s per design decision
      #1 (extends to 12s max on tap). */
  defaultDelaySec?: number;
  /** Maximum permitted delay before forced auto-advance. */
  maxDelaySec?: number;
}

/** Between-task rest screen — runs after a successful submit, before
    the next task's capture screen mounts. Tap-anywhere advances; if
    untouched, the timer auto-advances after `defaultDelaySec`. Any tap
    extends the timer (up to `maxDelaySec`), so a participant who taps
    once mid-breath has a moment longer to settle. */
export const GuidedRest: React.FC<GuidedRestProps> = ({
  done,
  total,
  onAdvance,
  defaultDelaySec = 6,
  maxDelaySec = 12,
}) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startedAtRef = useRef<number>(Date.now());
  const advancedRef = useRef(false);

  /** Fire onAdvance exactly once. */
  const fire = () => {
    if (advancedRef.current) return;
    advancedRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    onAdvance();
  };

  /** Schedule the auto-advance. If the participant taps to "extend", we
      reschedule for another defaultDelaySec but cap so total elapsed
      time never exceeds maxDelaySec. */
  const schedule = (delaySec: number) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(fire, delaySec * 1000);
  };

  useEffect(() => {
    startedAtRef.current = Date.now();
    schedule(defaultDelaySec);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Tap handler — single tap advances immediately (the participant said
      "I'm ready"). The "extend on tap" semantic in the open question was
      for an idle tap-anywhere extension; here tap-anywhere is also the
      explicit go-next gesture, so we treat it as advance. The max-delay
      cap is enforced by the schedule function in case we add future
      modes that extend on tap rather than advance. */
  const handleTap = () => {
    fire();
  };

  // Keep the schedule honest with the max-delay cap. If the participant
  // never taps, default fires. If a future change extends on tap, this
  // computes the remaining budget.
  const remainingBudgetSec = () =>
    Math.max(0, maxDelaySec - (Date.now() - startedAtRef.current) / 1000);
  // Reference to silence unused-var warning while the extension code
  // path is parked under open question #1.
  void remainingBudgetSec;

  return (
    <div
      className="gd-screen gd-screen--rest"
      onClick={handleTap}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleTap(); }}
      aria-label="Rest screen — tap to continue to the next task"
    >
      <div className="gd-progress-strip gd-progress-strip--dim">
        <GuidedProgressBar total={total} current={done} />
      </div>

      <div className="gd-rest-center">
        <div className="gd-rest-smudge" aria-hidden="true" />
        <h2>Take a breath.</h2>
        <p>Tap when ready to continue.</p>
      </div>

      <div className="gd-rest-hint" aria-hidden="true">
        <span className="gd-rest-pulse" />
        <span>Tap anywhere</span>
      </div>
    </div>
  );
};
